import { Attribute, Directive, ElementRef, forwardRef, HostListener, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LocationStrategy } from '@angular/common';
import { TOUCH_INTERACTION_DELAY_MS } from 'core/constants/touch-interaction';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[touchRouterLink]',
  standalone: true,
  providers: [
    {
      provide: RouterLink,
      useExisting: forwardRef(() => TouchRouterLinkDirective),
    },
  ],
})
export class TouchRouterLinkDirective extends RouterLink implements OnInit, OnDestroy {
  @Input()
  set touchRouterLink(commandsOrUrlTree: RouterLink['routerLink']) {
    this.routerLink = commandsOrUrlTree;
  }

  @Input() touchHoverDuration = 160;
  @Input() touchHoverClass = 'touch-hover';

  private lastTouchTime = 0;
  private touchDelayTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private removeHoverTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();
  private touchRenderer: Renderer2;
  private touchEl: ElementRef<HTMLElement>;
  private touchZone: NgZone;

  constructor(
    router: Router,
    route: ActivatedRoute,
    @Attribute('tabindex') tabIndexAttribute: string | null,
    renderer: Renderer2,
    el: ElementRef<HTMLElement>,
    locationStrategy: LocationStrategy,
    ngZone: NgZone,
  ) {
    super(router, route, tabIndexAttribute, renderer, el, locationStrategy);
    this.touchRenderer = renderer;
    this.touchEl = el;
    this.touchZone = ngZone;
  }

  ngOnInit(): void {
    const el = this.touchEl.nativeElement;
    this.touchZone.runOutsideAngular(() => {
      fromEvent<TouchEvent>(el, 'touchstart', { passive: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => this.onTouchStart(event));
      fromEvent<TouchEvent>(el, 'touchend', { passive: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.onTouchEnd());
      fromEvent<TouchEvent>(el, 'touchcancel', { passive: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.onTouchCancel());
    });
  }

  onTouchStart(event: TouchEvent): void {
    this.lastTouchTime = Date.now();
    if (event.touches.length > 1) {
      return;
    }
    if (this.removeHoverTimeoutId) {
      clearTimeout(this.removeHoverTimeoutId);
      this.removeHoverTimeoutId = null;
    }
    this.touchRenderer.addClass(this.touchEl.nativeElement, this.touchHoverClass);
  }

  onTouchEnd(): void {
    this.scheduleHoverRemoval();
  }

  onTouchCancel(): void {
    this.scheduleHoverRemoval();
  }

  @HostListener('click', ['$event.button', '$event.ctrlKey', '$event.shiftKey', '$event.altKey', '$event.metaKey'])
  override onClick(button: number, ctrlKey: boolean, shiftKey: boolean, altKey: boolean, metaKey: boolean): boolean {
    if (this.shouldDelayTouchClick()) {
      this.scheduleDelayedNavigation({ button, ctrlKey, shiftKey, altKey, metaKey });
      return false;
    }
    return super.onClick(button, ctrlKey, shiftKey, altKey, metaKey);
  }

  override ngOnDestroy(): void {
    if (this.touchDelayTimeoutId) {
      clearTimeout(this.touchDelayTimeoutId);
    }
    if (this.removeHoverTimeoutId) {
      clearTimeout(this.removeHoverTimeoutId);
    }
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  private shouldDelayTouchClick(): boolean {
    return Date.now() - this.lastTouchTime < TOUCH_INTERACTION_DELAY_MS;
  }

  private scheduleDelayedNavigation(click: {
    button: number;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    metaKey: boolean;
  }): void {
    if (this.touchDelayTimeoutId) {
      clearTimeout(this.touchDelayTimeoutId);
    }
    this.touchDelayTimeoutId = setTimeout(() => {
      super.onClick(click.button, click.ctrlKey, click.shiftKey, click.altKey, click.metaKey);
      this.touchDelayTimeoutId = null;
    }, TOUCH_INTERACTION_DELAY_MS);
  }

  private scheduleHoverRemoval(): void {
    if (this.removeHoverTimeoutId) {
      clearTimeout(this.removeHoverTimeoutId);
    }
    this.removeHoverTimeoutId = setTimeout(() => {
      this.touchRenderer.removeClass(this.touchEl.nativeElement, this.touchHoverClass);
      this.removeHoverTimeoutId = null;
    }, this.touchHoverDuration);
  }
}
